import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Button,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { validateToken, submitReviewWithToken, optOutFromEmails } from '../../services/reviewService';
import { ReviewTokenValidationDto, CreateReviewDto } from '../../types/review';
import { CreateReviewFormData } from '../../validation/reviewSchema';
import ReviewForm from '../../components/frontend/reviews/ReviewForm';
import { amazonColors } from '../../config/amazonTheme';

type PageState = 'loading' | 'valid' | 'invalid' | 'submitted' | 'opted-out';

const ReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasValidated = useRef(false);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenData, setTokenData] = useState<ReviewTokenValidationDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOptingOut, setIsOptingOut] = useState(false);

  const token = searchParams.get('token');
  const action = searchParams.get('action'); // 'unsubscribe' for opt-out
  // Support both /review?action=unsubscribe and /review/unsubscribe paths
  const isUnsubscribePath = location.pathname.includes('/unsubscribe');

  useEffect(() => {
    if (hasValidated.current) return;

    // Handle opt-out action (via query param or path)
    if ((action === 'unsubscribe' || isUnsubscribePath) && token) {
      hasValidated.current = true;
      handleOptOut();
      return;
    }

    // Validate token
    if (!token) {
      setPageState('invalid');
      setErrorMessage(t('reviews.invalidReviewLink'));
      return;
    }

    hasValidated.current = true;
    validateReviewToken();
  }, [token, action, isUnsubscribePath]);

  const validateReviewToken = async () => {
    try {
      const result = await validateToken(token!);
      if (result.isValid) {
        setTokenData(result);
        setPageState('valid');
      } else {
        setPageState('invalid');
        setErrorMessage(result.message || t('reviews.reviewLinkNoLongerValid'));
      }
    } catch {
      setPageState('invalid');
      setErrorMessage(t('reviews.verifyReviewLinkFailed'));
    }
  };

  const handleOptOut = async () => {
    setIsOptingOut(true);
    try {
      await optOutFromEmails(token!);
      setPageState('opted-out');
    } catch {
      setPageState('invalid');
      setErrorMessage(t('reviews.processRequestFailed'));
    } finally {
      setIsOptingOut(false);
    }
  };

  const handleSubmitReview = async (formData: CreateReviewFormData, photos?: File[]) => {
    if (!token) return;

    setIsSubmitting(true);
    try {
      const dto: CreateReviewDto = {
        token,
        rating: formData.rating,
        reviewerName: formData.reviewerName,
        comment: formData.comment || undefined,
        email: formData.email || undefined,
        highlights: formData.highlights,
        wouldOrderAgain: formData.wouldOrderAgain || undefined,
        agreeToPublish: formData.agreeToPublish,
      };

      await submitReviewWithToken(dto, photos);
      setPageState('submitted');
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading' || isOptingOut) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Invalid token state
  if (pageState === 'invalid') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <ErrorIcon
            sx={{
              fontSize: 64,
              color: 'error.main',
              mb: 2,
            }}
          />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t('reviews.reviewLinkInvalid')}
          </Typography>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {errorMessage}
          </Alert>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('reviews.reviewLinkExpiredDescription')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              bgcolor: amazonColors.orange,
              '&:hover': { bgcolor: amazonColors.orangeHover || '#e67a00' },
            }}
          >
            {t('reviews.goToHomepage')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Opted out state
  if (pageState === 'opted-out') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <CheckCircle
            sx={{
              fontSize: 64,
              color: 'success.main',
              mb: 2,
            }}
          />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t('reviews.unsubscribedSuccessfully')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('reviews.unsubscribedDescription')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              bgcolor: amazonColors.orange,
              '&:hover': { bgcolor: amazonColors.orangeHover || '#e67a00' },
            }}
          >
            {t('reviews.goToHomepage')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Submitted state
  if (pageState === 'submitted') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <CheckCircle
            sx={{
              fontSize: 64,
              color: 'success.main',
              mb: 2,
            }}
          />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t('reviews.thankYouForReview')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('reviews.feedbackHelpsOthers')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              bgcolor: amazonColors.orange,
              '&:hover': { bgcolor: amazonColors.orangeHover || '#e67a00' },
            }}
          >
            {t('cart.continueShopping')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Valid token - show review form
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          textAlign="center"
          gutterBottom
          sx={{ mb: 3 }}
        >
          {t('reviews.shareYourExperience')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 4 }}
        >
          {t('reviews.feedbackIsValuable')}
        </Typography>

        <ReviewForm
          orderNumber={tokenData?.orderNumber || ''}
          customerName={tokenData?.customerName}
          onSubmit={handleSubmitReview}
          isLoading={isSubmitting}
        />
      </Paper>
    </Container>
  );
};

export default ReviewPage;
